import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Inline } from '@atlaskit/primitives/compiled/inline';

import { buildLabelUrl } from '@confluence/macro-utils/entry-points/labelUrl.ts';

import type { PopularLabelData } from './popularLabelsTypes.ts';

const styles = cssMap({
	link: {
		textDecoration: 'none',
	},
});

// Match the legacy `.heatmap ul { line-height: 2em }` rule. Setting this on
// each anchor in `rem` (root-relative) instead of `em` (own-font-relative)
// gives every label a uniform ~32px line-height regardless of its own
// `fontSize`, so wrapped rows stay tight even when individual labels scale
// up to 1.75em — matching the legacy heatmap.
const HEATMAP_LINE_HEIGHT = '2rem';

// Legacy macro renders each label at `12 + rank * 2` px (popularlabels.vm).
// Express the same ramp in `em` so the heatmap stays relative to the host
// page's body font-size: assuming a 16px body, rank 1 → 0.875em (= 14px) and
// rank 8 → 1.75em (= 28px), matching the legacy output exactly.
const BASE_FONT_EM = 12 / 16;
const RANK_INCREMENT_EM = 2 / 16;

const getHeatmapFontSize = (rank: number): string => {
	return `${BASE_FONT_EM + rank * RANK_INCREMENT_EM}em`;
};

interface HeatmapLayoutProps {
	labels: PopularLabelData[];
	spaceKey?: string;
	/**
	 * Forwarded to each link's `target` attribute. Set to `'_blank'` in editor
	 * mode so author-side clicks open in a new tab; left undefined in view
	 * mode so the link navigates in place (legacy behavior).
	 */
	linkTarget?: '_blank';
}

export const HeatmapLayout = ({
	labels,
	spaceKey,
	linkTarget,
}: HeatmapLayoutProps): React.JSX.Element => {
	return (
		// Heatmap labels mix several font sizes — align them by text baseline so
		// they flow on the same line like running text (matching the legacy
		// macro), rather than being top-aligned as the Inline primitive defaults
		// to via flexbox.
		//
		// `space` applies to BOTH column and row gaps by default; that adds an
		// extra 8px between wrapped rows on top of each row's line-height,
		// making the layout feel sparse compared to legacy. Pin `rowSpace` to
		// zero so wrapped rows are spaced purely by each label's fixed
		// line-height (set inline via `HeatmapLabel`) — matching legacy's
		// behavior of fixing row height regardless of any single child's
		// font-size.
		<Inline
			space="space.100"
			rowSpace="space.0"
			shouldWrap
			alignBlock="baseline"
			testId="popular-labels-heatmap"
		>
			{labels.map((label) => (
				<Anchor
					key={label.labelId}
					href={buildLabelUrl(label.name, spaceKey)}
					target={linkTarget}
					xcss={styles.link}
					// The heatmap deliberately renders 8 different per-rank font-sizes
					// against a single fixed row height (legacy parity: `.heatmap ul {
					// line-height: 2em }` + `<li style="font-size:Npx">`). Typography
					// tokens bundle font-size and line-height together and can't model
					// this 1-to-many shape, so we set both properties inline.
					style={{
						fontSize: getHeatmapFontSize(label.rank),
						// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography, @atlaskit/ui-styling-standard/enforce-style-prop
						lineHeight: HEATMAP_LINE_HEIGHT,
					}}
				>
					{label.name}
				</Anchor>
			))}
		</Inline>
	);
};
